import React, { type ComponentProps, useMemo } from 'react'

import { type ColorToken, CssVarNames } from '../themes'
import {
  _InternalThemeProvider,
  getColorValue,
  useTheme,
} from './ThemeProvider'

type Props = {
  color: ColorToken | null | undefined
  children: ComponentProps<typeof _InternalThemeProvider>['children']
}

/**
 * Overrides the current theme’s text color for any components rendered in its DOM subtree.
 * Note that the colors set by this override will **NOT** carry into `Modals` and `Popovers`,
 * to ensure that modifying the text color of something that opens a `Modal` or `Popover` won't
 * affect the `Modal`/`Popover` itself. Use a `ThemeProvider` if you need styles to cross those
 * boundaries.
 *
 * See **[Theme Colors](/docs/product-theme-colors--page)** for color choice reference.
 */
export function TextColorOverride({ children, color }: Props) {
  const theme = useTheme()

  if (!color) {
    return <>{children({ __experimentalCssString: '', rootClassName: '' })}</>
  }

  return (
    <_InternalTextColorOverride textColor={getColorValue(color, theme)}>
      {children}
    </_InternalTextColorOverride>
  )
}

export function _InternalTextColorOverride({
  children,
  textColor,
}: {
  textColor: string | null
  children: ComponentProps<typeof _InternalThemeProvider>['children']
}) {
  const variables = useMemo(
    () => (textColor ? { [CssVarNames.textColor]: textColor } : null),
    [textColor]
  )

  if (!textColor) {
    return <>{children({ __experimentalCssString: '', rootClassName: '' })}</>
  }

  return (
    <_InternalThemeProvider variables={variables}>
      {children}
    </_InternalThemeProvider>
  )
}
